import { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '../../assets/logo.png';
import { translations } from '../../data/i18n';
import { siteConfig } from '../../data/site';

const Header = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1000;
  background: rgba(9, 8, 6, 0.94);
  backdrop-filter: blur(18px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Inner = styled.div`
  max-width: var(--container);
  height: var(--nav-height);
  margin: 0 auto;
  padding: 0 28px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 26px;

  @media (max-width: 760px) {
    padding: 0 18px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 285px;

  @media (max-width: 760px) {
    min-width: auto;
    gap: 12px;
  }
`;

const LogoBox = styled.div`
  width: 74px;
  height: 74px;
  background: var(--cream);
  border-radius: 13px;
  padding: 6px;
  box-shadow: 0 10px 28px rgba(0,0,0,0.25);

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 760px) {
    width: 58px;
    height: 58px;
  }
`;

const BrandText = styled.div`
  line-height: 1;

  strong {
    display: block;
    color: var(--white);
    font-size: 1.42rem;
    font-weight: 950;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  span {
    display: block;
    margin-top: 7px;
    color: var(--gold-light);
    font-size: 0.82rem;
    font-weight: 850;
  }

  @media (max-width: 760px) {
    strong {
      font-size: 1rem;
    }

    span {
      font-size: 0.68rem;
      max-width: 140px;
      line-height: 1.2;
    }
  }

  @media (max-width: 420px) {
    span {
      display: none;
    }
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 27px;
  flex: 1;

  button {
    position: relative;
    color: var(--white);
    background: transparent;
    padding: 0;
    font-size: 0.86rem;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    opacity: 0.92;
    cursor: pointer;
    transition: color 0.2s ease, opacity 0.2s ease;
  }

  button::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -9px;
    height: 2px;
    background: var(--gold);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.22s ease;
  }

  button:hover {
    color: var(--gold-light);
    opacity: 1;
  }

  button:hover::after {
    transform: scaleX(1);
  }

  @media (max-width: 1060px) {
    display: none;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LangButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  cursor: pointer;

  background: ${({ $active }) =>
    $active ? 'linear-gradient(135deg, var(--gold), var(--gold-light))' : 'rgba(255,255,255,0.12)'};

  color: #fff;
  font-weight: 950;
  box-shadow: ${({ $active }) =>
    $active ? '0 12px 26px rgba(221, 161, 47, 0.28)' : 'none'};

  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 520px) {
    width: 40px;
    height: 40px;
    font-size: 0.8rem;
  }
`;

const BookingButton = styled.button`
  margin-left: 10px;
  background: linear-gradient(135deg, var(--gold), var(--gold-light));
  color: var(--white);
  padding: 18px 28px;
  border-radius: 13px;
  font-weight: 950;
  text-transform: uppercase;
  box-shadow: 0 18px 38px rgba(221, 161, 47, 0.32);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 44px rgba(221, 161, 47, 0.42);
  }

  @media (max-width: 1060px) {
    display: none;
  }
`;

const MenuButton = styled.button`
  display: none;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  cursor: pointer;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.12);
  color: var(--white);
  position: relative;

  span {
    display: block;
    width: 22px;
    height: 2px;
    background: var(--white);
    border-radius: 99px;
    margin: 5px auto;
    transition: transform 0.22s ease, opacity 0.22s ease;
  }

  ${({ $open }) =>
    $open &&
    `
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    `}

  @media (max-width: 1060px) {
    display: block;
  }

  @media (max-width: 520px) {
    width: 44px;
    height: 44px;
  }
`;

const MobileOverlay = styled.div`
  display: none;

  @media (max-width: 1060px) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    position: fixed;
    inset: var(--nav-height) 0 0;
    background: rgba(0,0,0,0.54);
    backdrop-filter: blur(3px);
    z-index: 998;
  }
`;

const MobilePanel = styled.div`
  display: none;

  @media (max-width: 1060px) {
    display: block;
    position: fixed;
    top: var(--nav-height);
    left: 18px;
    right: 18px;
    z-index: 999;

    background:
      radial-gradient(circle at top right, rgba(221,161,47,0.18), transparent 36%),
      rgba(13, 11, 9, 0.98);

    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 0 0 24px 24px;
    box-shadow: 0 28px 80px rgba(0,0,0,0.42);
    padding: 22px;

    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-18px)')};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
    transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s ease;
  }
`;

const MobileNav = styled.nav`
  display: grid;
  gap: 10px;

  button {
    width: 100%;
    text-align: left;
    color: var(--white);
    padding: 15px 14px;
    border-radius: 14px;
    background: rgba(255,255,255,0.06);
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    cursor: pointer;
  }

  button:hover {
    background: rgba(221,161,47,0.18);
  }
`;

const MobileBooking = styled.button`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 14px;
  padding: 17px 18px;
  border-radius: 15px;
  background: linear-gradient(135deg, var(--gold), var(--gold-light));
  color: var(--white);
  font-weight: 950;
  text-transform: uppercase;
  cursor: pointer;
`;

const MobileMeta = styled.div`
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255,255,255,0.1);

  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  a {
    color: rgba(255,255,255,0.76);
    font-weight: 800;
  }
`;

function Navbar({ language, setLanguage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[language].nav;
  const mobileTabIndex = menuOpen ? 0 : -1;

  function closeMenu() {
    setMenuOpen(false);
  }

  function goToSection(sectionId) {
    closeMenu();

    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      return;
    }

    const element = document.getElementById(sectionId);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      navigate(`/#${sectionId}`);
    }
  }

  function goHome() {
    closeMenu();

    if (location.pathname !== '/') {
      navigate('/');
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  function switchLanguage(nextLanguage) {
    setLanguage(nextLanguage);
    closeMenu();
  }

  return (
    <>
      <Header>
        <Inner>
          <Brand to="/" aria-label={siteConfig.brandName} onClick={goHome}>
            <LogoBox>
              <img src={logo} alt={`${siteConfig.brandName} Logo`} />
            </LogoBox>

            <BrandText>
              <strong>{siteConfig.brandName}</strong>
              <span>{siteConfig.tagline}</span>
            </BrandText>
          </Brand>

          <DesktopNav>
            <button type="button" onClick={goHome}>{t.home}</button>
            <button type="button" onClick={() => goToSection('services')}>{t.services}</button>
            <button type="button" onClick={() => goToSection('gallery')}>{t.gallery}</button>
            <button type="button" onClick={() => goToSection('contact')}>{t.contact}</button>
          </DesktopNav>

          <Actions>
            <LangButton
              type="button"
              $active={language === 'de'}
              onClick={() => switchLanguage('de')}
              aria-label="Deutsch"
            >
              DE
            </LangButton>

            <LangButton
              type="button"
              $active={language === 'en'}
              onClick={() => switchLanguage('en')}
              aria-label="English"
            >
              EN
            </LangButton>

            <BookingButton type="button" onClick={() => goToSection('booking')}>
              {t.booking}
            </BookingButton>

            <MenuButton
              type="button"
              $open={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
              aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >
              <span />
              <span />
              <span />
            </MenuButton>
          </Actions>
        </Inner>
      </Header>

      <MobileOverlay $open={menuOpen} onClick={closeMenu} />

      <MobilePanel id="mobile-navigation" $open={menuOpen} aria-hidden={!menuOpen}>
        <MobileNav>
          <button type="button" tabIndex={mobileTabIndex} onClick={goHome}>{t.home}</button>
          <button type="button" tabIndex={mobileTabIndex} onClick={() => goToSection('services')}>{t.services}</button>
          <button type="button" tabIndex={mobileTabIndex} onClick={() => goToSection('gallery')}>{t.gallery}</button>
          <button type="button" tabIndex={mobileTabIndex} onClick={() => goToSection('contact')}>{t.contact}</button>
        </MobileNav>

        <MobileBooking type="button" tabIndex={mobileTabIndex} onClick={() => goToSection('booking')}>
          {t.booking}
        </MobileBooking>

        <MobileMeta>
          <Link to="/impressum" tabIndex={mobileTabIndex} onClick={closeMenu}>
            {translations[language].footer.imprint}
          </Link>
          <Link to="/datenschutz" tabIndex={mobileTabIndex} onClick={closeMenu}>
            {translations[language].footer.privacy}
          </Link>
        </MobileMeta>
      </MobilePanel>
    </>
  );
}

export default Navbar;
